import type {
  ChangeData,
  SaveCellData,
  StandardModule,
  StandardTableData,
} from '@/sites/project/pages/new-tr-review/components/standard-table/types';
import type { ModuleReportDataDetail } from '@/sites/project/pages/new-tr-review/report/types';
import type { ResponseType } from '@/types/common';
import httpUtil from '@/utils/http';

const request = httpUtil.mfd.request;
const prefix = 'api/v1/production';

export const trControlApi = {
  // feishu
  feishu() {
    return request({
      url: prefix + '/feishu',
      method: 'GET',
    });
  },

  // 更新节点配置
  updateNodeConfig(data: any): ResponseType {
    return request({
      url: prefix + '/tr/config',
      method: 'POST',
      data,
    });
  },
  // 获取节点配置
  getNodeConfig(projectID: number): ResponseType {
    return request({
      url: prefix + `/tr/config/${projectID}`,
      method: 'GET',
    });
  },
  // 更新评审要素
  updateChecklist(data: any): ResponseType {
    return request({
      url: prefix + '/tr/checkList',
      method: 'POST',
      data,
    });
  },

  // 获取评审要素
  getChecklist(reviewNode: string, page?: any): ResponseType {
    return request({
      url: prefix + `/tr/checkList`,
      method: 'GET',
      params: {
        reviewNode,
        page,
      },
    });
  },
  // 删除评审要素
  deleteChecklist(id: number): ResponseType {
    return request({
      url: prefix + `/tr/checkList/${id}`,
      method: 'DELETE',
    });
  },

  // 获取评审要素
  getChecklistById(reviewNodes: string[]): ResponseType {
    return request({
      url: prefix + `/tr/checkList/nodes`,
      method: 'GET',
      params: {
        reviewNodes,
      },
    });
  },

  // 添加评审记录
  addReviewRecord(data: any): ResponseType {
    return request({
      url: prefix + '/tr/record',
      method: 'POST',
      data,
    });
  },

  // 获取评审记录
  getReviewRecord(projectID: number, reviewNode: string): ResponseType {
    return request({
      url: prefix + `/tr/record/current/${projectID}`,
      method: 'GET',
      params: {
        reviewNode,
      },
    });
  },

  // 获取评审记录
  getReviewRecordById(recordID: number): ResponseType {
    return request({
      url: prefix + `/tr/record/${recordID}`,
      method: 'GET',
    });
  },

  // 获取评审历史记录
  getReviewHistory(projectID: number): ResponseType {
    return request({
      url: prefix + `/tr/record/history/${projectID}`,
      method: 'GET',
    });
  },

  // 导出内审数据
  exportAuditData(project: number[], startDate: number, endDate: number): any {
    return request({
      url: prefix + `/tr/record/export/audit`,
      method: 'POST',
      responseType: 'arraybuffer',
      data: {
        project,
        startDate,
        endDate,
      },
    });
  },

  // 导出外审数据
  exportExternalData(project: number[], startDate: number, endDate: number): any {
    return request({
      url: prefix + `/tr/record/export/external`,
      method: 'POST',
      responseType: 'arraybuffer',
      data: {
        project,
        startDate,
        endDate,
      },
    });
  },

  // 提交材料
  submitDeliverable(id: number, data: any): ResponseType {
    return request({
      url: prefix + `/tr/updateDv/${id}`,
      method: 'POST',
      data,
    });
  },

  // 评审要素自检
  selfCheck(data: { id: number; result: boolean; remarks: string; field: string }): ResponseType {
    return request({
      url: prefix + `/tr/selfCheck`,
      method: 'POST',
      data,
    });
  },

  // 提交风险问题
  submitRisk(recordId: number, data: any): ResponseType {
    return request({
      url: prefix + `/tr/risk/${recordId}`,
      method: 'POST',
      data,
    });
  },

  // 同步研发问题到评审遗留问题
  syncIssuesToRisk(recordId: number, data: any, options?: { signal?: AbortSignal }): ResponseType {
    return request({
      url: prefix + `/tr/risk/${recordId}/syncIssues`,
      method: 'POST',
      data,
      signal: options?.signal,
    });
  },

  // 获取风险问题列表
  getRiskList(recordId: number, issueType: string): ResponseType {
    return request({
      url: prefix + `/tr/risk/${recordId}`,
      method: 'GET',
      params: {
        issueType,
      },
    });
  },

  // 删除风险问题
  deleteRisk(id: number): ResponseType {
    return request({
      url: prefix + `/tr/risk/${id}`,
      method: 'DELETE',
    });
  },
  // 编辑风险问题
  editRisk(data: any): ResponseType {
    return request({
      url: prefix + `/tr/risk/edit`,
      method: 'PUT',
      data,
    });
  },

  // 材料审核
  DvCheck(recordId: number, data: any): ResponseType {
    return request({
      url: prefix + `/tr/record/check/${recordId}`,
      method: 'PUT',
      data,
    });
  },

  // 撤回外审
  CancelReview(recordId: number): ResponseType {
    return request({
      url: prefix + `/tr/record/cancel/${recordId}`,
      method: 'PUT',
    });
  },

  // 评审文件
  reviewFile(recordID: number, result: string): ResponseType {
    return request({
      url: prefix + `/tr/checkDv/${recordID}`,
      method: 'PUT',
      data: {
        result,
      },
    });
  },

  // 评审风险问题
  reviewRisk(recordID: number, result: string): ResponseType {
    return request({
      url: prefix + `/tr/risk/${recordID}`,
      method: 'PUT',
      data: {
        result,
      },
    });
  },

  // 完成评审
  finishReview(recordID: number, data: any): ResponseType {
    return request({
      url: prefix + `/tr/record/review/${recordID}`,
      method: 'PUT',
      data,
    });
  },

  // 添加报告
  addReport(recordId: number): ResponseType {
    return request({
      url: prefix + `/tr/report/${recordId}`,
      method: 'POST',
    });
  },

  // 根据recordId获取报告
  getReportByRecordId(recordId: number): ResponseType {
    return request({
      url: prefix + `/tr/report/recordId/${recordId}`,
      method: 'GET',
    });
  },

  // 编辑报告
  editReport(reportId: number, result: string, remarks: string): ResponseType {
    return request({
      url: prefix + `/tr/report/edit/${reportId}`,
      method: 'PUT',
      data: {
        result,
        remarks,
      },
    });
  },

  // 发布报告
  publishReport(reportId: number): ResponseType {
    return request({
      url: prefix + `/tr/report/release/${reportId}`,
      method: 'PUT',
    });
  },

  // 获取模块负责人配置
  getResponsible(projectID: number): ResponseType {
    return request({
      url: prefix + `/tr/module/responsible/${projectID}`,
      method: 'GET',
    });
  },

  // 编辑模块负责人配置
  editResponsible(id: number, data: any): ResponseType {
    return request({
      url: prefix + `/tr/module/responsible/${id}`,
      method: 'PUT',
      data,
    });
  },

  // 保存模块负责人配置
  saveResponsible(projectID: number, data: any): ResponseType {
    return request({
      url: prefix + `/tr/module/responsible/${projectID}`,
      method: 'POST',
      data,
    });
  },

  // 添加岗位人员配置
  addPosition(data: any): ResponseType {
    return request({
      url: prefix + '/tr/position',
      method: 'POST',
      data,
    });
  },

  // 获取岗位人员配置
  getPosition(projectID: number): ResponseType {
    return request({
      url: prefix + `/tr/position/${projectID}`,
      method: 'GET',
    });
  },

  // 删除岗位人员配置
  deletePosition(id: number): ResponseType {
    return request({
      url: prefix + `/tr/position/${id}`,
      method: 'DELETE',
    });
  },

  // 编辑岗位人员配置
  editPosition(id: number, data: any): ResponseType {
    return request({
      url: prefix + `/tr/position/${id}`,
      method: 'PUT',
      data,
    });
  },

  // 获取当前评审要素列表
  getCurrentChecklist(recordId: number): ResponseType {
    return request({
      url: prefix + `/tr/checkList/record/${recordId}`,
      method: 'GET',
    });
  },

  // 为当前recordId添加评审要素
  addChecklist(recordId: number, data: any): ResponseType {
    return request({
      url: prefix + `/tr/checkList/record/${recordId}`,
      method: 'POST',
      data,
    });
  },

  // 编辑评审要素
  editChecklist(id: number, data: any): ResponseType {
    return request({
      url: prefix + `/tr/checkList/record/${id}`,
      method: 'PUT',
      data,
    });
  },

  // 删除评审要素
  deleteChecklistByRecordId(id: number): ResponseType {
    return request({
      url: prefix + `/tr/checkList/record/${id}`,
      method: 'DELETE',
    });
  },

  // 获取质量目标
  getQualityGoal(projectId: number, reviewNode?: string): ResponseType {
    return request({
      url: prefix + `/tr/goal`,
      method: 'GET',
      params: {
        projectId,
        reviewNode,
      },
    });
  },

  // 添加质量目标
  addQualityGoal(data: any): ResponseType {
    return request({
      url: prefix + '/tr/goal',
      method: 'POST',
      data,
    });
  },

  // 编辑质量目标
  editQualityGoal(id: number, data: any): ResponseType {
    return request({
      url: prefix + `/tr/goal/${id}`,
      method: 'PUT',
      data,
    });
  },

  // 删除质量目标
  deleteQualityGoal(id: number): ResponseType {
    return request({
      url: prefix + `/tr/goal/${id}`,
      method: 'DELETE',
    });
  },

  // 发起评审
  launchReview(recordId: number, data: any): ResponseType {
    return request({
      url: prefix + `/tr/record/launch/${recordId}`,
      method: 'PUT',
      data,
    });
  },

  // 获取记录对应质量目标
  getQualityGoalByRecordId(recordId: number): ResponseType {
    return request({
      url: prefix + `/tr/goal/${recordId}`,
      method: 'GET',
    });
  },

  // 修改记录对应质量目标
  editQualityGoalByRecordId(id: number, data: any): ResponseType {
    return request({
      url: prefix + `/tr/goal/record/${id}`,
      method: 'PUT',
      data,
    });
  },

  // 同步质量目标
  syncQualityGoal(recordId: number): ResponseType {
    return request({
      url: prefix + `/tr/goal/sync/${recordId}`,
      method: 'GET',
    });
  },

  // 同步评审要素
  syncCheckList(recordId: number): ResponseType {
    return request({
      url: prefix + `/tr/checklist/sync/${recordId}`,
      method: 'GET',
    });
  },

  // 结束评审
  endReview(recordId: number, data: any): ResponseType {
    return request({
      url: prefix + `/tr/record/end/${recordId}`,
      method: 'PUT',
      data,
    });
  },

  // 发起投票
  launchVote(data: any): ResponseType {
    return request({
      url: prefix + `/tr/comment/launch/${data.recordId}`,
      method: 'POST',
      data,
    });
  },

  // 获取当前投票信息
  getCurrentComment(data: any): ResponseType {
    return request({
      url: prefix + `/tr/comment/user`,
      method: 'GET',
      params: data,
    });
  },

  // 更新投票信息
  updateComment(data: any): ResponseType {
    return request({
      url: prefix + '/tr/comment/edit',
      method: 'PUT',
      data,
    });
  },

  // 获取投票信息列表
  getCommentList(data: any): ResponseType {
    return request({
      url: prefix + `/tr/comment/list`,
      method: 'GET',
      params: data,
    });
  },

  // 新转接段评审评审标准获取
  getTrChecklist(data?: { version: number; standard: number }): ResponseType {
    return request({
      url: prefix + `/tr/checkList`,
      method: 'GET',
      params: data,
    });
  },

  getStandardList(): ResponseType {
    return request({
      url: prefix + `/tr/standard`,
      method: 'GET',
    });
  },

  // 	Version  int64 `form:"version"`
  // Standard int64  `form:"standard"`

  getStandardVersion(standard: number): ResponseType {
    return request({
      url: prefix + `/tr/standard/version/${standard}`,
      method: 'GET',
    });
  },

  updateStandardVersion(data: {
    id?: number;
    standardID?: number;
    version?: string;
    isReleased?: boolean;
    changeContent?: string;
  }): ResponseType {
    return request({
      url: prefix + `/tr/standard/version`,
      method: 'POST',
      data: data,
    });
  },

  // 修改标准的表头数据
  updateHead(data: any): ResponseType {
    return request({
      url: prefix + `/tr/head`,
      method: 'POST',
      data: data,
    });
  },

  // 更新变更流程中的表头数据
  AddModuleInUpdate(data: any): ResponseType {
    return request({
      url: prefix + `/tr/standard/controlled/addModule/${data.project}`,
      method: 'POST',
      data: data,
    });
  },

  // 更新变更流程中的表头数据
  AddStageInUpdate(data: any): ResponseType {
    return request({
      url: prefix + `/tr/standard/controlled/addStage/${data.project}`,
      method: 'POST',
      data: data,
    });
  },

  // 删除标准的表头数据
  deleteHead(id: number): ResponseType {
    return request({
      url: prefix + `/tr/head/${id}`,
      method: 'DELETE',
    });
  },

  // 更新版本数据
  updateCheckListItem(data: SaveCellData): ResponseType {
    return request({
      url: prefix + `/tr/checkList`,
      method: 'POST',
      data: data,
    });
  },

  // 更新标准
  updateStandard(data: { name: string }): ResponseType {
    return request({
      url: prefix + `/tr/standard`,
      method: 'POST',
      data: data,
    });
  },

  // 删除标准
  deleteStandard(id: number): ResponseType {
    return request({
      url: prefix + `/tr/standard/${id}`,
      method: 'DELETE',
    });
  },

  // 获取最新的评审标准
  getLatestCheckList(standard: number): ResponseType {
    return request({
      url: prefix + `/tr/checkList/latest/${standard}`,
      method: 'GET',
    });
  },

  // 删除受控中的评审标准模块
  deleteProjectCheckList(data: { project: number; moduleId: number; version: number; deleted: boolean }): ResponseType {
    return request({
      url: prefix + `/tr/checkList/project`,
      method: 'POST',
      data: data,
    });
  },

  // 删除受控中的评审标准阶段
  deleteProjectNode(data: { pkey: string; project: number; node: number; version: number }): ResponseType {
    return request({
      url: prefix + `/tr/standard/controlled/deleteStage/${data.project}`,
      method: 'DELETE',
      data: data,
    });
  },

  // 获取当前评审受控流程数据
  getProjectControlledStatus(project: number): ResponseType {
    return request({
      url: prefix + `/tr/standard/controlled/${project}`,
      method: 'GET',
    });
  },

  // 获取项目对应的评审标准
  getProjectStandardVersion(data: any): ResponseType {
    return request({
      url: prefix + `/tr/standard/:project/version`,
      method: 'POST',
      data: data,
    });
  },

  // 更新项目对应的评审标准
  updateProjectStandardVersion(data: any): ResponseType {
    return request({
      url: prefix + `/tr/standard/:project/version`,
      method: 'POST',
      data: data,
    });
  },

  // 绑定项目转阶段评审标准
  bindProjectStandard(data: {
    project: number;
    projectCode: string;
    pkey: string;
    Standard: StandardTableData[];
    UserList: StandardModule[];
  }): ResponseType {
    return request({
      url: prefix + `/tr/standard/controlled/bind/${data.project}`,
      method: 'POST',
      data: data,
    });
  },

  // 获取通用模块list
  getModuleList(data?: any): ResponseType {
    return request({
      url: prefix + `/tr/module`,
      method: 'GET',
      params: data,
    });
  },

  getModuleListByStandard(standard: number): ResponseType {
    return request({
      url: prefix + `/tr/head/latest/${standard}`,
      method: 'GET',
    });
  },

  createModule(data: any): ResponseType {
    return request({
      url: prefix + `/tr/module`,
      method: 'POST',
      data: data,
    });
  },

  updateModule(data: any): ResponseType {
    return request({
      url: prefix + `/tr/module`,
      method: 'PUT',
      data: data,
    });
  },

  deleteModule(id: number): ResponseType {
    return request({
      url: prefix + `/tr/module/${id}`,
      method: 'DELETE',
    });
  },

  // 通过ids获获取模块list
  getModuleListByIds(moduleIds: number[]): ResponseType {
    return request({
      url: prefix + `/tr/moduleIds`,
      method: 'POST',
      data: { moduleIds: moduleIds },
    });
  },

  // 获取标准受控的操作日志
  getProjectControlledLogs(controlledId: number): ResponseType {
    return request({
      url: prefix + `/tr/standard/controlled/log/${controlledId}`,
      method: 'GET',
    });
  },

  // 获取项目标准
  getProjectCheckList(params: { project: number; node?: number[]; head?: number[]; version?: number }): ResponseType {
    return request({
      url: prefix + `/tr/checkList/project`,
      method: 'GET',
      params,
    });
  },

  // 修改项目标准
  updateProjectCheckList(data: { project: number; tableData: SaveCellData }): ResponseType {
    return request({
      url: prefix + `/tr/checkList/project/${data.project}`,
      method: 'POST',
      data: data.tableData,
    });
  },

  // 获取当前项目version_list
  getProjectVersionList(project: number): ResponseType {
    return request({
      url: prefix + `/tr/checkList/project/version/${project}`,
      method: 'GET',
    });
  },

  // 催办接口
  urgeModuleUser(data: { projectCode: string; pkey: string; userIds: number[] }): ResponseType {
    return request({
      url: prefix + `/tr/standard/controlled/urge`,
      method: 'POST',
      data,
    });
  },

  // 项目模块确认
  projectEditConfirm(data: { project: number; module: number; confirm: boolean }): ResponseType {
    return request({
      url: prefix + `/tr/standard/controlled/confirm`,
      method: 'POST',
      data,
    });
  },

  // 发起审批接口
  initiateApproval(data: {
    project: number;
    pkey: string;
    projectCode: string;
    reviewer: number[]; // 铁四角人员
  }): ResponseType {
    return request({
      url: prefix + `/tr/standard/controlled/approval`,
      method: 'POST',
      data,
    });
  },

  // 审批接口
  approval(data: {
    project: number;
    projectCode: string;
    pkey: string;
    result: boolean;
    content: string;
    module: number[];
  }): ResponseType {
    return request({
      url: prefix + `/tr/standard/controlled/confirm/approval`,
      method: 'POST',
      data,
    });
  },

  // 铁四角审批催办
  urgeReviewer(data: { projectCode: string; pkey: string; userIds: number[]; isChange: boolean }): ResponseType {
    return request({
      url: prefix + `/tr/standard/controlled/urge/reviewer`,
      method: 'POST',
      data,
    });
  },

  // 发起标准变更流程
  initiateChange(data: { project: number; projectCode: string; pkey: string }): ResponseType {
    return request({
      url: prefix + `/tr/standard/controlled/change`,
      method: 'POST',
      data,
    });
  },

  // 取消变更流程
  cancelUpdate(data: { version: number; project: number }): ResponseType {
    return request({
      url: prefix + `/tr/standard/controlled/change/cancel`,
      method: 'POST',
      data,
    });
  },

  // 创建标准更新日志
  getProjectUpdateLogs(params: { versionId: number }): ResponseType {
    return request({
      url: prefix + `/tr/checkList/project/updateLog`,
      method: 'GET',
      params,
    });
  },

  // 创建标准更新日志
  createProjectUpdateLog(data: ChangeData): ResponseType {
    return request({
      url: prefix + `/tr/checkList/project/updateLog`,
      method: 'POST',
      data,
    });
  },

  // 获取新的评审要素接口
  getRecordCommentUser(data: { recordId: number; type: string[] }): ResponseType {
    return request({
      url: prefix + `/tr/comment/user`,
      method: 'GET',
      params: data,
    });
  },

  // 获取新的评审要素接口
  getRecordCommentList(data: { recordId: number; type: string[] }): ResponseType {
    return request({
      url: prefix + `/tr/comment/list`,
      method: 'GET',
      params: data,
    });
  },

  // 批量更新项目人员评论信息
  BatchUpdateRecordComment(data: any[]): ResponseType {
    return request({
      url: prefix + `/tr/comment/batchEdit`,
      method: 'PUT',
      data,
    });
  },

  // 检查是否有其他用户编辑
  checkCommentIsEditing(data: any): ResponseType {
    return request({
      url: prefix + `/tr/comment/checkEdit`,
      method: 'POST',
      data,
    });
  },

  // 批量更新模块评审要素
  batchUpdateCheckListForRecord(data: ModuleReportDataDetail[]): ResponseType {
    return request({
      url: prefix + `/tr/checkList/batchRecord`,
      method: 'PUT',
      data,
    });
  },

  // 获取项目下一阶段
  getProjectNextStage(projectId: number): ResponseType {
    return request({
      url: prefix + `/tr/project/nextStage/${projectId}`,
      method: 'GET',
    });
  },

  // 发布
  release(data: { project: number; projectCode: string; pkey: string }): ResponseType {
    return request({
      url: prefix + `/tr/standard/controlled/change/release`,
      method: 'POST',
      data,
    });
  },

  // 更新会议日期
  updateMeetingDate(recordId: number, meetingDate: string): ResponseType {
    return request({
      url: prefix + `/tr/record/meeting`,
      method: 'POST',
      data: {
        recordId,
        meetingDate,
      },
    });
  },
};
